import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Search, Package, RotateCcw, Undo2, XCircle, CheckCircle2, Clock,
  ChevronRight, Loader2, X, RefreshCw, Truck, AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';

// ─────────────────────────────────────────────────────────────────────────
// Seller RMA (Cancel / Exchange / Return / Refund Integrated Management).
// Follows the CustomersSection layout pattern. No changes to sidebar-parent.
// ─────────────────────────────────────────────────────────────────────────

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const digits = (s) => (s || '').replace(/\D/g, '');
const fmtDate = (d) => { if (!d) return '-'; try { return format(new Date(d), 'yyyy.MM.dd HH:mm', { locale: ko }); } catch { return '-'; } };

const TYPE_TABS = [
  { k: '', label: '전체' },
  { k: 'cancel', label: '취소' },
  { k: 'exchange', label: '교환' },
  { k: 'return', label: '반품' },
];

const STATUS_TABS = [
  { k: '', label: '전체' },
  { k: 'requested', label: '신청' },
  { k: 'in_progress', label: '처리중' },
  { k: 'terminal', label: '종료' },
];

const REASON_LABEL = {
  buyer_change_of_mind: '단순 변심',
  product_defect: '상품 불량',
  wrong_delivery: '오배송',
  damaged: '파손 · 하자',
  buyer_choice: '구매자 취소',
  other: '기타',
};

const STATUS_COLOR = (s) => {
  if (['completed'].includes(s)) return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300';
  if (['rejected', 'withdrawn'].includes(s)) return 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-white/5 dark:border-white/10';
  if (s === 'approved') return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300';
  if (['pickup_in_progress', 'received', 'reshipping', 'refund_processing'].includes(s)) return 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:border-violet-500/30 dark:text-violet-300';
  return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300';
};

const TypeBadge = ({ type, label }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-black ${
    type === 'cancel'   ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300' :
    type === 'exchange' ? 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-300' :
    'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:border-purple-500/30 dark:text-purple-300'
  }`} data-testid={`rma-type-${type}`}>{label}</span>
);

const StatusBadge = ({ status, label }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-black ${STATUS_COLOR(status)}`} data-testid={`rma-status-${status}`}>{label}</span>
);

// ── Root ──────────────────────────────────────────────────────────────────
export default function SellerRmaSection() {
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (type) params.type = type;
      if (status) params.status = status;
      if (q) params.q = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [rL, rS] = await Promise.all([
        api.get('/seller/rma', { params }),
        api.get('/seller/rma/summary'),
      ]);
      setRows(rL.data?.items || []);
      setSummary(rS.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '요청 목록을 불러오지 못했습니다.');
    } finally { setLoading(false); }
  }, [type, status, q, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const totals = summary?.totals || {};
  const inProgress = summary?.in_progress || 0;

  return (
    <div className="space-y-4" data-testid="seller-rma-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>취소·교환·반품 관리</h2>
          <p className="text-xs text-gray-500 mt-1">
            결제완료 이후 취소·교환·반품 요청을 통합 관리합니다.
            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-black">
              <AlertCircle size={10} /> TEST MODE · 실 환불 API 미연결
            </span>
          </p>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-40" data-testid="rma-refresh">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> 새로고침
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="처리 중" value={inProgress} icon={Clock} tone="amber" testId="rma-sum-in-progress" />
        <SummaryCard label="신청" value={totals.requested || 0} icon={Package} tone="amber" testId="rma-sum-requested" />
        <SummaryCard label="승인" value={totals.approved || 0} icon={CheckCircle2} tone="blue" testId="rma-sum-approved" />
        <SummaryCard label="완료" value={totals.completed || 0} icon={CheckCircle2} tone="emerald" testId="rma-sum-completed" />
        <SummaryCard label="반려" value={totals.rejected || 0} icon={XCircle} tone="gray" testId="rma-sum-rejected" />
      </div>

      {/* Filter tabs */}
      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 space-y-3">
        <div className="flex flex-wrap gap-1" data-testid="rma-type-tabs">
          {TYPE_TABS.map((t) => (
            <button key={t.k || 'all'} onClick={() => setType(t.k)} data-testid={`rma-type-tab-${t.k || 'all'}`}
              className={`px-3 py-1.5 rounded-full text-xs font-black border ${type === t.k ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}>
              {t.label}
              {t.k && summary?.by_type?.[t.k] && (
                <span className="ml-1 text-[10px] opacity-70">
                  {Object.values(summary.by_type[t.k] || {}).reduce((a, b) => a + b, 0)}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1" data-testid="rma-status-tabs">
          {STATUS_TABS.map((t) => (
            <button key={t.k || 'all'} onClick={() => setStatus(t.k)} data-testid={`rma-status-tab-${t.k || 'all'}`}
              className={`px-3 py-1.5 rounded-full text-xs font-black border ${status === t.k ? 'bg-black text-white dark:bg-white dark:text-black border-black dark:border-white' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="주문번호 · 고객명 · 전화번호 · 상품명"
              className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="rma-search-input" />
          </div>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="rma-date-from" />
          <span className="text-xs text-gray-400">~</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="rma-date-to" />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-16 text-center text-sm text-gray-400"><Loader2 className="animate-spin inline mr-1" size={14}/> 불러오는 중…</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="rma-empty">
          <Package className="mx-auto text-gray-300" size={28} />
          <p className="mt-3 text-sm font-bold">해당 조건의 요청이 없습니다.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="rma-list">
          {rows.map((r) => (
            <RmaRow key={r.rma_id} row={r} onOpen={() => setDetailId(r.rma_id)} />
          ))}
        </div>
      )}

      {detailId && <RmaDetailDrawer rmaId={detailId} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}

const SummaryCard = ({ label, value, icon: Icon, tone, testId }) => {
  const toneCls = tone === 'emerald' ? 'text-emerald-700 dark:text-emerald-400' :
    tone === 'blue' ? 'text-blue-700 dark:text-blue-400' :
    tone === 'amber' ? 'text-amber-700 dark:text-amber-400' :
    'text-gray-600 dark:text-gray-400';
  return (
    <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-2xl p-4" data-testid={testId}>
      <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
        <Icon size={12} className={toneCls} /> {label}
      </div>
      <p className={`text-2xl font-black mt-1 ${toneCls}`}>{value.toLocaleString('ko-KR')}</p>
    </div>
  );
};

const RmaRow = ({ row, onOpen }) => (
  <button onClick={onOpen} className="w-full text-left bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 rounded-2xl p-4 hover:border-[#FF3B30]/40 transition-colors" data-testid={`rma-row-${row.rma_id}`}>
    <div className="flex items-center justify-between gap-2 mb-2">
      <div className="flex items-center gap-2 flex-wrap">
        <TypeBadge type={row.type} label={row.type_label} />
        <StatusBadge status={row.status} label={row.status_label} />
        <span className="text-xs text-gray-500">주문번호 <span className="font-bold text-gray-700 dark:text-gray-200">{row.order_number}</span></span>
      </div>
      <ChevronRight size={14} className="text-gray-400" />
    </div>
    <div className="flex items-center justify-between gap-2 text-xs">
      <div className="min-w-0">
        <p className="truncate"><span className="text-gray-500">고객: </span><span className="font-bold">{row.buyer_snapshot?.name || '-'}</span> · {row.buyer_snapshot?.phone || '-'}</p>
        <p className="truncate text-gray-500 mt-0.5">
          {(row.items || []).slice(0, 2).map((it) => `${it.name_snapshot || '-'} × ${it.quantity}`).join(', ')}
          {(row.items || []).length > 2 && ` 외 ${row.items.length - 2}건`}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-black text-sm">{won(row.refund_calc?.total)}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(row.created_at)}</p>
      </div>
    </div>
    {row.reason_code && (
      <p className="text-[11px] text-gray-500 mt-2 truncate">사유: {REASON_LABEL[row.reason_code] || row.reason_code}{row.reason_note ? ` · ${row.reason_note}` : ''}</p>
    )}
  </button>
);

// ── Detail Drawer ─────────────────────────────────────────────────────────
function RmaDetailDrawer({ rmaId, onClose, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupForm, setPickupForm] = useState({ courier: 'CJ대한통운', tracking_number: '' });
  const [reshipOpen, setReshipOpen] = useState(false);
  const [reshipForm, setReshipForm] = useState({ courier: 'CJ대한통운', tracking_number: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/seller/rma/${rmaId}`);
      setDoc(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '불러오기 실패'); onClose(); }
    finally { setLoading(false); }
  }, [rmaId, onClose]);
  useEffect(() => { load(); }, [load]);

  const doApprove = async () => {
    if (!window.confirm(`요청을 승인하시겠습니까?\n${doc.type === 'cancel' ? '취소 승인 후 자동으로 환불 처리 상태로 이동합니다.' : ''}`)) return;
    setBusy(true);
    try {
      await api.post(`/seller/rma/${rmaId}/approve`, {});
      toast.success('요청을 승인했습니다.');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '승인 실패'); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error('반려 사유를 입력해주세요.'); return; }
    setBusy(true);
    try {
      await api.post(`/seller/rma/${rmaId}/reject`, { reason: rejectReason.trim() });
      toast.success('요청을 반려했습니다.');
      setRejectOpen(false); setRejectReason('');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '반려 실패'); }
    finally { setBusy(false); }
  };

  const doStatus = async (to, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/seller/rma/${rmaId}/status`, { to, ...extra });
      toast.success('상태가 변경되었습니다.');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '상태 변경 실패'); }
    finally { setBusy(false); }
  };

  const doPickup = async () => {
    if (!pickupForm.tracking_number.trim()) { toast.error('회수 송장번호를 입력해주세요.'); return; }
    await doStatus('pickup_in_progress', { pickup: pickupForm });
    setPickupOpen(false); setPickupForm({ courier: 'CJ대한통운', tracking_number: '' });
  };

  const doReship = async () => {
    if (!reshipForm.tracking_number.trim()) { toast.error('재배송 송장번호를 입력해주세요.'); return; }
    await doStatus('reshipping', { reshipment: reshipForm });
    setReshipOpen(false); setReshipForm({ courier: 'CJ대한통운', tracking_number: '' });
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="rma-detail-drawer">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div className="w-full max-w-xl bg-white dark:bg-[#0A0A0A] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-white dark:bg-[#0A0A0A] border-b border-black/10 dark:border-white/10 p-4 flex items-center justify-between">
          <p className="text-sm font-black">요청 상세</p>
          <button onClick={onClose} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10" data-testid="rma-detail-close"><X size={16}/></button>
        </div>
        {loading || !doc ? (
          <div className="p-12 text-center text-sm text-gray-400"><Loader2 className="animate-spin inline mr-1" size={14}/> 불러오는 중…</div>
        ) : (
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <TypeBadge type={doc.type} label={doc.type_label} />
              <StatusBadge status={doc.status} label={doc.status_label} />
              <span className="text-xs text-gray-500">RMA {doc.rma_id}</span>
            </div>

            <SectionCard title="주문 정보">
              <Row label="주문번호" value={doc.order_number} testId="rma-detail-order-number" />
              <Row label="판매자 주문" value={doc.seller_order_id} mono />
              <Row label="요청 시각" value={fmtDate(doc.created_at)} />
              {doc.approved_at && <Row label="승인 시각" value={fmtDate(doc.approved_at)} />}
              {doc.rejected_at && <Row label="반려 시각" value={fmtDate(doc.rejected_at)} />}
              {doc.completed_at && <Row label="완료 시각" value={fmtDate(doc.completed_at)} />}
            </SectionCard>

            <SectionCard title="구매자">
              <Row label="이름" value={doc.buyer_snapshot?.name || '-'} testId="rma-detail-buyer-name" />
              <Row label="전화" value={doc.buyer_snapshot?.phone || '-'} testId="rma-detail-buyer-phone" />
              <Row label="이메일" value={doc.buyer_snapshot?.email || '-'} />
            </SectionCard>

            <SectionCard title="요청 상품">
              <div className="space-y-2">
                {(doc.items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <p className="font-bold truncate">{it.name_snapshot}</p>
                      {Object.keys(it.option || {}).length > 0 && (
                        <p className="text-[10px] text-gray-500">{Object.entries(it.option).map(([k,v]) => `${k}: ${v}`).join(' · ')}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p>{won(it.unit_price)} × {it.quantity}</p>
                      <p className="font-bold">{won(it.line_total)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="사유">
              <Row label="사유 유형" value={REASON_LABEL[doc.reason_code] || doc.reason_code} testId="rma-detail-reason-code" />
              {doc.reason_note && <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-wrap">{doc.reason_note}</p>}
              <Row label="배송비 부담" value={
                doc.shipping_burden === 'seller_fault' ? '판매자 귀책' :
                doc.shipping_burden === 'buyer_fault' ? '구매자 부담' : doc.shipping_burden
              } />
            </SectionCard>

            <SectionCard title="환불 계산 (Mock)" testId="rma-detail-refund">
              <Row label="상품 금액" value={won(doc.refund_calc?.product_total)} />
              <Row label="배송비 환급" value={won(doc.refund_calc?.refund_shipping)} />
              <Row label="배송비 차감" value={`− ${won(doc.refund_calc?.shipping_deduct)}`} />
              <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-black/10 dark:border-white/10">
                <span className="font-bold">환불 예정 금액</span>
                <span className="font-black text-[#FF3B30]" data-testid="rma-detail-refund-total">{won(doc.refund_calc?.total)}</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">※ 실제 결제 취소 API 미연결 (Mock 계산)</p>
            </SectionCard>

            {doc.pickup && (
              <SectionCard title="회수 정보">
                <Row label="택배사" value={doc.pickup.courier} />
                <Row label="송장번호" value={doc.pickup.tracking_number} mono testId="rma-detail-pickup-tracking" />
                <Row label="요청일시" value={fmtDate(doc.pickup.requested_at)} />
              </SectionCard>
            )}

            {doc.reshipment && (
              <SectionCard title="재배송 정보">
                <Row label="택배사" value={doc.reshipment.courier} />
                <Row label="송장번호" value={doc.reshipment.tracking_number} mono testId="rma-detail-reship-tracking" />
                <Row label="발송일시" value={fmtDate(doc.reshipment.shipped_at)} />
              </SectionCard>
            )}

            {doc.reject_reason && (
              <SectionCard title="반려 사유"><p className="text-xs text-red-600 dark:text-red-400">{doc.reject_reason}</p></SectionCard>
            )}

            <SectionCard title="처리 이력">
              <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="rma-detail-history">
                {(doc.history || []).map((h) => (
                  <div key={h.history_id} className="flex items-start gap-2 text-[11px]">
                    <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded font-black border ${STATUS_COLOR(h.action)}`}>{h.action}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-500">{fmtDate(h.at)}{h.actor?.name ? ` · ${h.actor.name}` : ''}</p>
                      {h.note && <p className="text-gray-600 dark:text-gray-300 truncate">{h.note}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ACTION BUTTONS */}
            <ActionButtons doc={doc} busy={busy}
              onApprove={doApprove}
              onReject={() => setRejectOpen(true)}
              onPickup={() => setPickupOpen(true)}
              onReceive={() => doStatus('received')}
              onReship={() => setReshipOpen(true)}
              onComplete={() => { if (window.confirm('완료 처리하시겠습니까?')) doStatus('completed'); }}
              onRefundProcessing={() => doStatus('refund_processing')} />
          </div>
        )}
      </div>

      {rejectOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
             onClick={() => setRejectOpen(false)}
             onKeyDown={(e) => { if (e.key === 'Escape') setRejectOpen(false); }}
             tabIndex={-1}>
          <div className="w-full max-w-sm bg-white dark:bg-[#141414] rounded-2xl p-5 space-y-3"
               onClick={(e) => e.stopPropagation()} data-testid="rma-reject-modal">
            <p className="text-sm font-black">반려 사유 입력 (필수)</p>
            <textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setRejectOpen(false); }} autoFocus
              placeholder="반려 사유를 입력해주세요." className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="rma-reject-reason-input" />
            <div className="flex gap-2">
              <button onClick={() => setRejectOpen(false)} className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold" data-testid="rma-reject-cancel">취소</button>
              <button onClick={doReject} disabled={busy || !rejectReason.trim()} className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40" data-testid="rma-reject-confirm">반려 확정</button>
            </div>
          </div>
        </div>
      )}

      {pickupOpen && (
        <TrackingModal title="회수 송장 입력" form={pickupForm} setForm={setPickupForm}
          onClose={() => setPickupOpen(false)} onSubmit={doPickup} busy={busy} testIdPrefix="rma-pickup" />
      )}
      {reshipOpen && (
        <TrackingModal title="재배송 송장 입력" form={reshipForm} setForm={setReshipForm}
          onClose={() => setReshipOpen(false)} onSubmit={doReship} busy={busy} testIdPrefix="rma-reship" />
      )}
    </div>
  );
}

const SectionCard = ({ title, children, testId }) => (
  <div className="rounded-xl border border-black/10 dark:border-white/10 p-3" data-testid={testId}>
    <p className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-2">{title}</p>
    {children}
  </div>
);
const Row = ({ label, value, mono, testId }) => (
  <div className="flex items-center justify-between text-xs py-0.5">
    <span className="text-gray-500">{label}</span>
    <span className={`font-bold ${mono ? 'font-mono' : ''}`} data-testid={testId}>{value}</span>
  </div>
);

const TrackingModal = ({ title, form, setForm, onClose, onSubmit, busy, testIdPrefix }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
    <div className="w-full max-w-sm bg-white dark:bg-[#141414] rounded-2xl p-5 space-y-3" data-testid={`${testIdPrefix}-modal`}>
      <p className="text-sm font-black">{title}</p>
      <input value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })} placeholder="택배사"
        className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid={`${testIdPrefix}-courier`} />
      <input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} placeholder="송장번호"
        className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent font-mono" data-testid={`${testIdPrefix}-tracking`} />
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold">취소</button>
        <button onClick={onSubmit} disabled={busy} className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40" data-testid={`${testIdPrefix}-submit`}>저장</button>
      </div>
    </div>
  </div>
);

const ActionButtons = ({ doc, busy, onApprove, onReject, onPickup, onReceive, onReship, onComplete, onRefundProcessing }) => {
  const s = doc.status; const t = doc.type;
  const btn = "py-2 rounded-full text-xs font-black disabled:opacity-40 transition-colors";
  if (['completed', 'rejected', 'withdrawn'].includes(s)) {
    return <div className="text-xs text-gray-500 text-center py-2" data-testid="rma-actions-terminal">종료된 요청입니다.</div>;
  }
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="rma-detail-actions">
      {s === 'requested' && (
        <>
          <button onClick={onApprove} disabled={busy} className={`${btn} bg-[#FF3B30] text-white`} data-testid="rma-approve-btn">
            <CheckCircle2 className="inline mr-1" size={12} /> 승인
          </button>
          <button onClick={onReject} disabled={busy} className={`${btn} border border-black/10 dark:border-white/10`} data-testid="rma-reject-btn">
            <XCircle className="inline mr-1" size={12} /> 반려
          </button>
        </>
      )}
      {s === 'approved' && t === 'cancel' && (
        <button onClick={onRefundProcessing} disabled={busy} className={`${btn} bg-blue-600 text-white col-span-2`} data-testid="rma-refund-processing-btn">환불 처리 시작</button>
      )}
      {s === 'approved' && (t === 'return' || t === 'exchange') && (
        <button onClick={onPickup} disabled={busy} className={`${btn} bg-violet-600 text-white col-span-2`} data-testid="rma-pickup-btn">
          <Truck className="inline mr-1" size={12} /> 회수 시작 (송장 입력)
        </button>
      )}
      {s === 'pickup_in_progress' && (
        <button onClick={onReceive} disabled={busy} className={`${btn} bg-violet-600 text-white col-span-2`} data-testid="rma-receive-btn">입고 완료 처리</button>
      )}
      {s === 'received' && t === 'exchange' && (
        <button onClick={onReship} disabled={busy} className={`${btn} bg-[#FF3B30] text-white col-span-2`} data-testid="rma-reship-btn">
          <Truck className="inline mr-1" size={12} /> 재배송 시작 (송장 입력)
        </button>
      )}
      {s === 'received' && t === 'return' && (
        <button onClick={onRefundProcessing} disabled={busy} className={`${btn} bg-blue-600 text-white col-span-2`} data-testid="rma-refund-processing-btn">환불 처리 시작</button>
      )}
      {s === 'reshipping' && (
        <button onClick={onComplete} disabled={busy} className={`${btn} bg-emerald-600 text-white col-span-2`} data-testid="rma-complete-btn">교환 완료 처리</button>
      )}
      {s === 'refund_processing' && (
        <button onClick={onComplete} disabled={busy} className={`${btn} bg-emerald-600 text-white col-span-2`} data-testid="rma-complete-btn">환불 완료 처리</button>
      )}
    </div>
  );
};
